import { useSyncExternalStore } from 'react';
import { MotifModal } from '@ey-xd/motif-wc-react';
import { idleManager, type IdleState } from '@/auth/idle';

/**
 * Idle-warning modal — M2.11.
 *
 * Mirrors the legacy `session-idle.component.html`. Shown when the user has
 * been inactive for `T_idle` (1720s); user has `T_warn` (60s) to click
 * "Stay signed in" before auto-logout. Footer also has explicit Sign out.
 *
 * Mounted at shell level; renders nothing while `status === 'inactive'`.
 */
export function SessionIdleModal() {
  const state = useSyncExternalStore<IdleState>(
    (cb) => idleManager.subscribe(cb),
    () => idleManager.getState(),
    () => idleManager.getState(),
  );

  const open = state.status === 'warned';
  const secondsLeft = Math.max(0, Math.ceil(state.remainingMs / 1000));

  return (
    <div className="motif-theme-light">
      <MotifModal
        open={open}
        onModalClose={() => idleManager.continueSession()}
        size="md"
        showFeaturedIcon={false}
        style={
          {
            '--modal-overflow-y': 'hidden',
            '--modal-body-overflow-y': 'hidden',
            '--modal-footer-width': '100%',
          } as React.CSSProperties
        }
      >
        <div slot="header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              border: '2px solid #d32f2f',
              color: '#d32f2f',
              fontWeight: 'bold',
              fontSize: '18px',
            }}
          >
            &#9432;
          </span>
          <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>No Activity Detected !!</span>
        </div>
        <div slot="body">
          <p style={{ margin: '0 0 16px' }}>You have been idle for 29 minutes ...</p>
          <p style={{ margin: '0 0 16px' }}>
            You can continue your session by clicking{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={() => idleManager.continueSession()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') idleManager.continueSession();
              }}
              style={{
                backgroundColor: '#4caf50',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              CONTINUE
            </span>{' '}
            Or you can Logout by clicking{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={() => idleManager.expireNow()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') idleManager.expireNow();
              }}
              style={{
                backgroundColor: '#f44336',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              LOGOUT
            </span>
          </p>
          <p style={{ margin: 0 }}>
            If no action is taken, then your session will be terminated automatically in{' '}
            <b style={{ color: '#d32f2f', fontSize: '1.1rem' }}>{secondsLeft}</b> seconds for
            security reasons.
          </p>
        </div>
      </MotifModal>
    </div>
  );
}
